import React, { useEffect, useState } from 'react';
import { FileText, Download, Upload, X, Plus, Image, Pencil, Save, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import html2canvas from 'html2canvas';

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  email: string;
  loan_amount: number;
  installments: number;
  total_amount: number;
  status: string;
  approval_status: 'pending' | 'approved' | 'rejected';
  payment_status: 'pending' | 'paid';
  rejection_reason?: string;
  resubmitted_at?: string;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  ruta: number;
  pix_key: string;
  bank: string;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  installments_data: {
    id: string;
    due_date: string;
    amount: number;
    status: string;
  }[];
}

interface EditingContract {
  id: string;
  client_name: string;
}

export default function MyContracts() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [filter, setFilter] = useState<'approved' | 'rejected'>('approved');
  const [resubmitting, setResubmitting] = useState(false);
  const [editingContract, setEditingContract] = useState<EditingContract | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchContracts();
  }, [filter, user]);

  const fetchContracts = async () => {
    if (!user) return;
    
    try {
      // Calculate date 15 days ago
      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
      
      const { data, error } = await supabase
        .from('contracts')
        .select(`
          *,
          contract_files(*),
          installments_data:installments(*)
        `)
        .eq('seller_id', user?.id)
        .eq('unit_id', user?.unit_id)
        .eq('approval_status', filter)
        .gte('created_at', fifteenDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      setContracts(data || []);
    } catch (err) {
      setError('Erro ao carregar contratos');
      console.error('Error fetching contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = async (contractId: string) => {
    setResubmitting(true);
    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          approval_status: 'pending',
          resubmitted_at: new Date().toISOString(),
          approved_at: null,
          approved_by: null,
          rejection_reason: null
        })
        .eq('id', contractId);

      if (error) throw error;

      setContracts(contracts.filter(contract => contract.id !== contractId));
      setSelectedContract(null);
    } catch (err) {
      console.error('Error resubmitting contract:', err);
      setError('Erro ao reenviar contrato');
    } finally {
      setResubmitting(false);
    }
  };

  const handleGenerateImage = async (contract: Contract) => {
    await navigator.clipboard.writeText(contract.pix_key);

    const approvalDate = new Date(contract.approved_at!);
    const formattedDate = approvalDate.toLocaleDateString('pt-BR');
    const formattedTime = approvalDate.toLocaleTimeString('pt-BR');

    const tempDiv = document.createElement('div');
    tempDiv.style.padding = '20px';
    tempDiv.style.background = 'white';
    tempDiv.style.width = '400px';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    
    tempDiv.innerHTML = `
      <div style="border: 2px solid #4CAF50; border-radius: 10px; padding: 20px; text-align: center;">
        <h2 style="color: #2E7D32; margin-bottom: 20px; font-size: 24px;">Contrato Aprovado</h2>
        
        <div style="text-align: left; margin-bottom: 20px;">
          <p style="margin: 10px 0;"><strong>Nome:</strong> ${contract.client_name}</p>
          <p style="margin: 10px 0;"><strong>CPF:</strong> ${contract.cpf}</p>
          <p style="margin: 10px 0;"><strong>Data da Venda:</strong> ${new Date(contract.created_at).toLocaleDateString('pt-BR')}</p>
          <p style="margin: 10px 0;"><strong>Chave PIX:</strong> ${contract.pix_key}</p>
          <p style="margin: 10px 0;"><strong>Banco:</strong> ${contract.bank}</p>
          <p style="margin: 10px 0;"><strong>Valor do Empréstimo:</strong> ${formatCurrency(contract.loan_amount - 10)}</p>
        </div>

        <div style="background-color: #E8F5E9; border-radius: 8px; padding: 15px; margin-top: 20px;">
          <p style="color: #2E7D32; margin: 0;">Aprovado em ${formattedDate} às ${formattedTime}</p>
        </div>

        <div style="margin-top: 20px;">
          <img src="https://i.imgur.com/1QY7Iep.png" alt="Selo de Aprovação" style="width: 100px; height: 100px;" />
        </div>
      </div>
    `;

    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv);
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `contrato-aprovado-${contract.client_name}.png`;
      link.click();
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  const handleEditClientName = async () => {
    if (!editingContract) return;

    try {
      const { error } = await supabase
        .from('contracts')
        .update({ client_name: editingContract.client_name })
        .eq('id', editingContract.id);

      if (error) throw error;

      setContracts(contracts.map(contract => 
        contract.id === editingContract.id 
          ? { ...contract, client_name: editingContract.client_name }
          : contract
      ));

      setEditingContract(null);
    } catch (err) {
      console.error('Error updating client name:', err);
      setError('Erro ao atualizar nome do cliente');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    return status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Meus Contratos</h1>
        <div className="text-right">
          <p className="text-sm text-gray-600">Exibindo contratos dos últimos 15 dias</p>
          <p className="text-xs text-gray-500">
            {new Date(new Date().setDate(new Date().getDate() - 15)).toLocaleDateString('pt-BR')} até {new Date().toLocaleDateString('pt-BR')}
          </p>
        </div>
      </div>

      {/* Period Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <p className="text-sm text-blue-800">
            <strong>Período de exibição:</strong> Esta tela mostra apenas os contratos criados nos últimos 15 dias para melhor performance.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-md transition-colors ${
              filter === 'approved'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Aprovados
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-md transition-colors ${
              filter === 'rejected'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Recusados
          </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor do Empréstimo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parcelas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pagamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ruta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract) => (
                <tr 
                  key={contract.id}
                  className={contract.payment_status === 'paid' ? 'bg-green-50' : ''}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {contract.approval_status === 'approved' && (
                        <button
                          onClick={() => handleGenerateImage(contract)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Gerar Imagem"
                        >
                          <Image className="w-5 h-5" />
                        </button>
                      )}
                      {filter === 'rejected' && (
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => handleResubmit(contract.id)}
                          disabled={resubmitting}
                        >
                          <RefreshCw className={`w-5 h-5 ${resubmitting ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {editingContract?.id === contract.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editingContract.client_name}
                            onChange={(e) => setEditingContract({
                              ...editingContract,
                              client_name: e.target.value
                            })}
                            className="px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <button
                            onClick={handleEditClientName}
                            className="text-green-600 hover:text-green-800"
                          >
                            <Save className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setEditingContract(null)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm font-medium text-gray-900">
                            {contract.client_name}
                          </span>
                          <button
                            onClick={() => setEditingContract({
                              id: contract.id,
                              client_name: contract.client_name
                            })}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(contract.loan_amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(contract.total_amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.installments}x
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                      {contract.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusColor(contract.payment_status)}`}>
                      {contract.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.ruta}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(contract.created_at).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}